import { useLocalSearchParams } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import type { DancerRef } from "@recital-master/shared";

import { AppHeader } from "@/components/AppHeader";
import { SearchInput } from "@/components/SearchInput";
import { useAccount } from "@/data/AccountProvider";
import { useCatalog, usePerformances } from "@/data/CatalogProvider";
import { useAppRouter } from "@/lib/appRouter";
import { recitalPath, useRecitalBySlug } from "@/lib/routes";
import { PinkChip } from "@/components/PinkChip";
import { Button } from "@/design/Button";
import { HeartIcon } from "@/design/icons";
import { Divider, Screen } from "@/design/primitives";
import { Muted, Overline, Text } from "@/design/Text";
import { palette, radius, space } from "@/design/tokens";
import { useReadinessHold } from "@/providers/ReadinessGate";
import { myDancerPlayLabel } from "@/playback/myDancer";
import { recitalMyDancerWatchPath } from "@/playback/watchRoute";

/** One dancer across every performance of the recital. */
type RecitalDancer = DancerRef & {
  danceCount: number;
};

function DancerRow({
  dancer,
  saved,
  onToggle
}: {
  dancer: RecitalDancer;
  saved: boolean;
  onToggle: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={saved ? `Remove ${dancer.name}` : `Save ${dancer.name}`}
      onPress={onToggle}
      style={({ pressed, hovered, focused }) => [
        styles.dancerRow,
        (hovered || focused) && styles.dancerRowHovered,
        pressed && styles.dancerRowPressed
      ]}
    >
      <View style={[styles.heart, saved && styles.heartSaved]}>
        <HeartIcon size={15} color={saved ? palette.inkOnAccent : palette.faint} />
      </View>
      <View style={styles.dancerBody}>
        <Text variant="danceTitle">{dancer.name}</Text>
        <Muted variant="small">
          {dancer.danceCount} {dancer.danceCount === 1 ? "dance" : "dances"}
        </Muted>
      </View>
      <Text variant="smallStrong" color={saved ? palette.pink : palette.faint}>
        {saved ? "Saved" : "Save"}
      </Text>
    </Pressable>
  );
}

export default function ShowDancerPickerScreen() {
  const { studioSlug, recitalSlug } = useLocalSearchParams<{
    studioSlug: string;
    recitalSlug: string;
  }>();
  const router = useAppRouter();
  const recital = useRecitalBySlug(studioSlug, recitalSlug);
  const performances = usePerformances(recital?.recitalId);
  const { catalog, performanceDetailErrors, preparePerformance, status } = useCatalog();
  const { savedDancers, saveDancer, removeDancer } = useAccount();
  const [query, setQuery] = useState("");
  const performanceDataSettled = performances.every(
    (performance) => catalog?.dances[performance.performanceId] || performanceDetailErrors[performance.performanceId]
  );
  useReadinessHold(
    `dancer-details-${studioSlug ?? "unknown"}-${recitalSlug ?? "unknown"}`,
    status === "ready" && (!recital || performanceDataSettled)
  );

  useEffect(() => {
    for (const performance of performances) void preparePerformance(performance.performanceId).catch(() => {});
  }, [performances, preparePerformance]);

  // Merge every performance's dancer index: one entry per dancer for the recital.
  const recitalDancers = useMemo<RecitalDancer[]>(() => {
    const byId = new Map<string, RecitalDancer>();
    for (const performance of performances) {
      const index = catalog?.dancerIndexes[performance.performanceId];
      for (const entry of index?.dancers ?? []) {
        const existing = byId.get(entry.dancerId) ?? {
          dancerId: entry.dancerId,
          name: entry.name,
          slug: entry.slug,
          danceCount: 0
        };
        existing.danceCount += entry.danceIds.length;
        byId.set(entry.dancerId, existing);
      }
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [catalog, performances]);

  const savedIds = useMemo(
    () => new Set(savedDancers.map((d) => d.dancerId)),
    [savedDancers]
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? recitalDancers.filter((d) => d.name.toLowerCase().includes(q)) : recitalDancers;
  }, [recitalDancers, query]);
  const savedHere = recitalDancers.filter((d) => savedIds.has(d.dancerId));

  if (!recital) {
    if (status !== "ready") return null;
    return (
      <Screen header={<AppHeader back backHref={recital ? recitalPath(recital) : undefined} />}>
        <View style={styles.missing}>
          <Text variant="title">This recital isn’t in your library.</Text>
          <Muted>Everything you can watch lives in your recitals.</Muted>
          <Button
            label="Go to my recitals"
            variant="secondary"
            onPress={() => router.replace("/recitals")}
          />
        </View>
      </Screen>
    );
  }

  /** Plays every saved dancer routine across the complete recital. */
  const playSaved = () => {
    void Promise.all(performances.map((performance) => preparePerformance(performance.performanceId).catch(() => {})))
      .then(() => router.push(recitalMyDancerWatchPath(recital.recitalId) as never));
  };

  return (
    <Screen
      header={<AppHeader back backHref={recital ? recitalPath(recital) : undefined} />}
      keyboardAware
      keyboardShouldPersistTaps="handled"
    >
      <View style={styles.header}>
        <Overline>My Dancers</Overline>
        <Text variant="display">Find your dancer</Text>
        <Muted>
          Save your dancer once and every recital gathers their dances — no
          scrubbing, no hunting across shows.
        </Muted>
      </View>

      <SearchInput
        value={query}
        onChangeText={setQuery}
        placeholder="Type your dancer’s name…"
      />

      <View style={styles.list}>
        {filtered.length === 0 ? (
          <Muted style={styles.empty}>
            No dancers match “{query}”. Names come from the studio’s program —
            try a first name on its own.
          </Muted>
        ) : (
          filtered.map((dancer) => (
            <DancerRow
              key={dancer.dancerId}
              dancer={dancer}
              saved={savedIds.has(dancer.dancerId)}
              onToggle={() =>
                savedIds.has(dancer.dancerId)
                  ? removeDancer(dancer.dancerId)
                  : saveDancer(dancer, recital.recitalId)
              }
            />
          ))
        )}
      </View>

      {savedHere.length > 0 ? (
        <View style={styles.footer}>
          <Divider />
          <Text variant="body" color={palette.textSoft} style={styles.footerText}>
            {savedHere.length === 1
              ? `${savedHere[0].name} dances in ${savedHere[0].danceCount} ${savedHere[0].danceCount === 1 ? "number" : "numbers"} this season.`
              : `${savedHere.length} dancers saved.`}
          </Text>
          <PinkChip
            label={myDancerPlayLabel(savedHere)}
            size="lg"
            onPress={playSaved}
          />
        </View>
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  header: {
    gap: space.sm,
    marginBottom: space.xl,
    marginTop: 0
  },
  list: {
    marginTop: space.lg
  },
  empty: {
    paddingVertical: space.xl,
    textAlign: "center"
  },
  dancerRow: {
    alignItems: "center",
    borderRadius: radius.md,
    flexDirection: "row",
    gap: space.lg,
    marginHorizontal: -space.md,
    paddingHorizontal: space.md,
    paddingVertical: space.md
  },
  dancerRowHovered: {
    backgroundColor: palette.coolWashFaint
  },
  dancerRowPressed: {
    opacity: 0.85
  },
  heart: {
    alignItems: "center",
    borderColor: palette.hairlineStrong,
    borderRadius: 999,
    borderWidth: 1,
    height: 36,
    justifyContent: "center",
    width: 36
  },
  heartSaved: {
    backgroundColor: palette.pink,
    borderColor: palette.pink
  },
  dancerBody: {
    flex: 1,
    gap: 1
  },
  footer: {
    gap: space.lg,
    marginTop: space.xl,
    paddingBottom: space.xl
  },
  footerText: {
    textAlign: "center"
  },
  missing: {
    alignItems: "flex-start",
    gap: space.lg,
    marginTop: space.xl
  }
});
